/**
 * Whether a draft still holds what it was opened with.
 *
 * This is what decides whether cancelling asks first and whether leaving the screen is guarded, so
 * it has to answer "nothing was actually changed" correctly rather than approximately. Referential
 * equality cannot: every one of these forms replaces its object on each keystroke, so a field typed
 * and typed back would leave a screen claiming unsaved work that does not exist, and a guard that
 * fires when nothing changed is a guard people learn to click through.
 *
 * JSON.stringify cannot either. Going back to an inherited theme field removes the key rather than
 * setting it to undefined, so the same six fields arrive in a different order, and two drafts that
 * hold the same values would compare as different strings.
 *
 * A key holding undefined is treated as absent for the same reason: the drafts here say "nothing
 * set at this level" both ways round, and only one of the two survives a save.
 */
export function sameDraft(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every((item, at) => sameDraft(item, right[at]));
  }

  if (typeof left !== 'object' || typeof right !== 'object' || left === null || right === null) {
    return false;
  }

  const a = present(left as Record<string, unknown>);
  const b = present(right as Record<string, unknown>);
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => Object.hasOwn(b, key) && sameDraft(a[key], b[key]));
}

/** The same object without the keys holding undefined, which are absences rather than values. */
function present(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, held]) => held !== undefined));
}
